import React from "react";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { SelectInput, MenuOption } from "../../common/selectOptions";
import { useGetStats } from "../queries/statsQueries";
import { getStatsColumns, getStatsMobileColumns } from "./statsTableConfig";
import { SPORT_TYPE_OPTIONS, StatTypeOption } from "./types";
import { SitesResponse, SportsResponse } from "../../common/store/types";
import DataGrid from "../../common/Table/newTable";

interface SiteOption {
  readonly value: string;
  readonly label: string;
}

const SITE_OPTIONS: SiteOption[] = [
  { value: 'DK', label: 'DraftKings' },
  { value: 'FD', label: 'FanDuel' },
];

// Types that require site selection
const SITE_REQUIRED_TYPES = ['topstacks'];

interface StatsProps {
  readonly selectedSport: SportsResponse;
  readonly selectedSite?: SitesResponse;
  readonly tableHeight?: string;
  readonly backgroundColor?: string;
  readonly isUserSubscribed?: boolean;
}

const Stats = (props: StatsProps) => {
  const { selectedSport, selectedSite, tableHeight, backgroundColor, isUserSubscribed = true } = props;

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Extract sport string from Redux prop
  const sport = selectedSport?.sport || '';

  // State for type selection
  const [selectedType, setSelectedType] = React.useState<string>("");

  // State for site selection (used for topstacks types)
  const [selectedSiteLocal, setSelectedSiteLocal] = React.useState<string>("DK");

  // Get available types for selected sport
  const availableTypes = React.useMemo(() => {
    const sportConfig = SPORT_TYPE_OPTIONS.find(
      (s) => s.sport === sport
    );
    return sportConfig?.types || [];
  }, [sport]);

  // Reset type when sport changes
  React.useEffect(() => {
    const sportConfig = SPORT_TYPE_OPTIONS.find(
      (s) => s.sport === sport
    );
    if (sportConfig && sportConfig.types.length > 0) {
      setSelectedType(sportConfig.types[0].value);
    }
  }, [sport]);

  // Update local site when selectedSite prop changes
  React.useEffect(() => {
    if (selectedSite?.site) {
      setSelectedSiteLocal(selectedSite.site);
    }
  }, [selectedSite]);

  // Check if current type requires site selection
  const requiresSite = SITE_REQUIRED_TYPES.includes(selectedType);

  // Determine which site to use for the query
  const siteForQuery = requiresSite ? selectedSiteLocal : undefined;

  // Fetch stats data - disable if user is not subscribed
  const {
    data: statsData,
    isLoading,
    error,
  } = useGetStats(
    sport,
    selectedType,
    isUserSubscribed && !!sport && !!selectedType,
    siteForQuery
  );

  // Get columns based on current selection and device type
  const columns = React.useMemo(
    () => isMobile
      ? getStatsMobileColumns(sport, selectedType)
      : getStatsColumns(sport, selectedType),
    [sport, selectedType, isMobile]
  );

  // Type selector options
  const typeOptions: MenuOption<StatTypeOption>[] = availableTypes.map((t) => ({
    name: t.label,
    value: t.value,
    option: t,
  }));

  // Site selector options
  const siteOptions: MenuOption<SiteOption>[] = SITE_OPTIONS.map((s) => ({
    name: s.label,
    value: s.value,
    option: s,
  }));

  // Transform data to include proper id field for DataGrid
  const rows = React.useMemo(() => {
    return (statsData || []).map((row: any, index: number) => ({
      ...row,
      id: row.id || index + 1,
      nameAndId: row.name || `${index + 1}`,
    }));
  }, [statsData]);

  // Handle subscription check
  if (!isUserSubscribed) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={tableHeight || "100%"}
        sx={{ backgroundColor: backgroundColor || "rgba(125, 125, 125, .25)" }}
      >
        Please subscribe to access stats data.
      </Box>
    );
  }

  // Determine if we should show the selector bar
  const showSelectorBar = availableTypes.length > 1 || requiresSite;

  return (
    <Box height={tableHeight || "100%"}>
      {/* Type/Site Selector - Above Table */}
      {showSelectorBar && (
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={isMobile ? 1 : 2}
          sx={{
            padding: isMobile ? "8px 12px" : "12px 16px",
            backgroundColor: backgroundColor || "rgba(125, 125, 125, .25)",
          }}
        >
          {availableTypes.length > 1 && (
            <SelectInput
              data={typeOptions}
              label="Stat Type"
              value={selectedType}
              onSelect={(selection: StatTypeOption) =>
                setSelectedType(selection.value)
              }
            />
          )}
          {requiresSite && (
            <SelectInput
              data={siteOptions}
              label="Site"
              value={selectedSiteLocal}
              onSelect={(selection: SiteOption) =>
                setSelectedSiteLocal(selection.value)
              }
            />
          )}
        </Stack>
      )}

      {/* Data Table */}
      <Box
        sx={{
          height: showSelectorBar ? "calc(100% - 80px)" : "100%",
          backgroundColor: backgroundColor || "rgba(125, 125, 125, .25)",
        }}
      >
        <DataGrid
          lineups={rows}
          headers={columns}
          isLoading={isLoading}
          selectedView="stats"
          simType="dataHub"
          includeStacks={false}
          setStackView={() => {}}
          tierlocked={false}
          setPlayerList={() => {}}
          setStackList={() => {}}
          pagination
          rowHeight={isMobile ? 52 : 52}
          fontSize={isMobile ? 12 : 14}
          emptyText={error ? "Error loading stats. Please try again." : `No stats available for ${sport} ${selectedType}.`}
          backgroundColor={backgroundColor || "rgba(125, 125, 125, .25)"}
          isMobile={isMobile}
          pinnedColumnsLeft={isMobile ? ["name"] : undefined}
        />
      </Box>
    </Box>
  );
};

export default Stats;
